import { useState, useEffect } from 'react';
import { SellerLayout } from '../components/SellerLayout';
import { StripeOnboardingSection } from '../components/StripeOnboardingSection';
import { RequestPayoutModal } from '../components/RequestPayoutModal';
import { PaginationControls } from '../components/PaginationControls';
import { getSellerBalanceSummary, getSellerLedgerEntries, getSellerPayouts, getStripeConnectStatus } from '../api/client';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';
import { colors } from '../constants/colors';
import { parseUTC } from '../utils/date';
import type { SellerBalanceSummary, SellerLedgerEntry, SellerPayout, StripeConnectStatus } from '../types';

type TabView = 'history' | 'payouts';

const LEDGER_GRID = '1fr 1fr 2fr 1fr 1fr';
const PAYOUTS_GRID = '1fr 1.5fr 1fr 1fr';

export function SellerPayoutsPage() {
  useDocumentTitle('Payouts');
  const isMobile = useIsMobile();
  const [balance, setBalance] = useState<SellerBalanceSummary | null>(null);
  const [ledgerEntries, setLedgerEntries] = useState<SellerLedgerEntry[]>([]);
  const [payouts, setPayouts] = useState<SellerPayout[]>([]);
  const [stripeStatus, setStripeStatus] = useState<StripeConnectStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLedgerLoading, setIsLedgerLoading] = useState(true);
  const [isPayoutsLoading, setIsPayoutsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabView>('history');
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerTotalPages, setLedgerTotalPages] = useState(1);
  const [payoutsPage, setPayoutsPage] = useState(1);
  const [payoutsTotalPages, setPayoutsTotalPages] = useState(1);

  useEffect(() => {
    loadBalance();
    loadStripeStatus();
  }, []);

  useEffect(() => {
    if (activeTab === 'payouts') {
      loadPayouts(payoutsPage);
    }
  }, [activeTab, payoutsPage]);

  useEffect(() => {
    loadLedger(ledgerPage);
  }, [ledgerPage]);

  const loadBalance = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getSellerBalanceSummary();
      setBalance(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load balance');
    } finally {
      setIsLoading(false);
    }
  };

  const loadLedger = async (page: number = 1) => {
    try {
      setIsLedgerLoading(true);
      const data = await getSellerLedgerEntries(page - 1, 20);
      setLedgerEntries(data.content);
      setLedgerTotalPages(data.totalPages);
    } catch (err) {
      console.error('Failed to load ledger:', err);
    } finally {
      setIsLedgerLoading(false);
    }
  };

  const loadPayouts = async (page: number = 1) => {
    try {
      setIsPayoutsLoading(true);
      const data = await getSellerPayouts(page - 1, 20);
      setPayouts(data.content);
      setPayoutsTotalPages(data.totalPages);
    } catch (err) {
      console.error('Failed to load payouts:', err);
    } finally {
      setIsPayoutsLoading(false);
    }
  };

  const loadStripeStatus = async () => {
    try {
      const data = await getStripeConnectStatus();
      setStripeStatus(data);
    } catch (err) {
      console.error('Failed to load Stripe status:', err);
    }
  };

  const handlePayoutSuccess = () => {
    setShowPayoutModal(false);
    loadBalance();
    setLedgerPage(1);
    loadLedger(1);
    if (activeTab === 'payouts') {
      setPayoutsPage(1);
      loadPayouts(1);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return parseUTC(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getOriginLabel = (origin: string) => {
    switch (origin) {
      case 'ORDER': return 'Sale';
      case 'REFUND': return 'Refund';
      case 'REFUND_FEE': return 'Fee Credit';
      case 'PAYOUT': return 'Payout';
      case 'ADJUSTMENT': return 'Adjustment';
      default: return origin;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING': return 'Pending';
      case 'COMPLETE': return 'Complete';
      case 'PAID': return 'Paid';
      case 'FAILED': return 'Failed';
      case 'CANCELLED': return 'Cancelled';
      default: return status;
    }
  };

  const getPayoutStatusStyle = (status: string): React.CSSProperties => {
    switch (status) {
      case 'PAID':
        return { backgroundColor: '#D1FAE5', color: '#059669' };
      case 'FAILED':
        return { backgroundColor: '#FEE2E2', color: '#DC2626' };
      case 'CANCELLED':
        return { backgroundColor: colors.gray[100], color: colors.gray[600] };
      default:
        return { backgroundColor: '#FEF3C7', color: '#D97706' };
    }
  };

  const canRequestPayout = balance && balance.availableBalance > 0
    && stripeStatus?.payoutsEnabled;

  return (
    <SellerLayout>
      <div style={styles.container}>
        {/* Top Row: Balance (60%) + Status (40%) */}
        <div style={{ ...styles.topRow, gridTemplateColumns: isMobile ? '1fr' : '3fr 2fr' }}>
          {/* Balance Section */}
          <div style={styles.balanceSection}>
            <h2 style={styles.sectionTitle}>Balance</h2>
            {isLoading ? (
              <div style={styles.loading}>Loading balance...</div>
            ) : error ? (
              <div style={styles.error}>{error}</div>
            ) : balance ? (
              <>
                <div style={styles.balanceRow}>
                  <div style={styles.totalBalance}>{formatCurrency(balance.totalBalance)}</div>
                  {canRequestPayout && (
                    <button
                      style={styles.payoutButton}
                      onClick={() => setShowPayoutModal(true)}
                    >
                      Request Payout
                    </button>
                  )}
                </div>
                <div style={styles.detailsCard}>
                  <div style={styles.detailRow}>
                    <div style={styles.detailLine}>
                      <span style={styles.detailAmount}>{formatCurrency(balance.availableBalance)}</span>
                      <span style={styles.detailLabel}>available for payout</span>
                    </div>
                    <div style={styles.detailHint}>
                      These funds are available to initiate payout to your bank account.
                    </div>
                  </div>
                  <div style={styles.divider} />
                  <div style={styles.detailRow}>
                    <div style={styles.detailLine}>
                      <span style={styles.detailAmount}>{formatCurrency(balance.pendingBalance)}</span>
                      <span style={styles.detailLabel}>processing</span>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>

          {/* Status Section */}
          <StripeOnboardingSection title="Status" />
        </div>

        {/* Tab Selector */}
        <div style={styles.tabRow}>
          <button
            style={{
              ...styles.tabButton,
              ...(activeTab === 'history' ? styles.tabButtonActive : {}),
            }}
            onClick={() => setActiveTab('history')}
          >
            Balance History
          </button>
          <button
            style={{
              ...styles.tabButton,
              ...(activeTab === 'payouts' ? styles.tabButtonActive : {}),
            }}
            onClick={() => setActiveTab('payouts')}
          >
            Payouts
          </button>
        </div>

        {/* Table */}
        {activeTab === 'history' ? (
            /* Balance History Table */
            isLedgerLoading ? (
              <div style={styles.loading}>Loading transactions...</div>
            ) : ledgerEntries.length === 0 ? (
              <div style={styles.emptyState}>No transactions yet</div>
            ) : (
              <>
                <div style={{ overflowX: isMobile ? 'auto' : undefined, WebkitOverflowScrolling: 'touch' }}>
                <div style={{ ...styles.ledgerTable, minWidth: isMobile ? '800px' : undefined }}>
                  <div style={{ ...styles.ledgerHeader, gridTemplateColumns: LEDGER_GRID }}>
                    <span>Date</span>
                    <span>Type</span>
                    <span>Description</span>
                    <span>Status</span>
                    <span style={{ textAlign: 'right' }}>Amount</span>
                  </div>
                  {ledgerEntries.map((entry) => (
                    <div key={entry.id} style={{ ...styles.ledgerRow, gridTemplateColumns: LEDGER_GRID }}>
                      <span style={styles.ledgerCell}>{formatDate(entry.createdAt)}</span>
                      <span style={styles.ledgerCell}>{getOriginLabel(entry.origin)}</span>
                      <span style={styles.ledgerCell}>{entry.description}</span>
                      <span>
                        <span style={{
                          ...styles.statusBadge,
                          backgroundColor: entry.status === 'COMPLETE' ? '#D1FAE5' :
                            entry.status === 'FAILED' ? '#FEE2E2' : '#FEF3C7',
                          color: entry.status === 'COMPLETE' ? '#059669' :
                            entry.status === 'FAILED' ? '#DC2626' : '#D97706',
                        }}>
                          {getStatusLabel(entry.status)}
                        </span>
                      </span>
                      <span style={{
                        ...styles.ledgerCell,
                        textAlign: 'right',
                        fontWeight: 600,
                        color: entry.entryType === 'CREDIT' ? '#059669' : '#DC2626',
                      }}>
                        {entry.entryType === 'DEBIT' ? '-' : '+'}{formatCurrency(entry.amount)}
                      </span>
                    </div>
                  ))}
                </div>
                </div>
                <PaginationControls
                  currentPage={ledgerPage}
                  totalPages={ledgerTotalPages}
                  onPageChange={setLedgerPage}
                />
              </>
            )
          ) : (
            /* Payouts Table */
            isPayoutsLoading ? (
              <div style={styles.loading}>Loading payouts...</div>
            ) : payouts.length === 0 ? (
              <div style={styles.emptyState}>No payouts yet</div>
            ) : (
              <>
                <div style={{ overflowX: isMobile ? 'auto' : undefined, WebkitOverflowScrolling: 'touch' }}>
                <div style={{ ...styles.ledgerTable, minWidth: isMobile ? '800px' : undefined }}>
                  <div style={{ ...styles.ledgerHeader, gridTemplateColumns: PAYOUTS_GRID }}>
                    <span>Date</span>
                    <span>Bank Account</span>
                    <span>Status</span>
                    <span style={{ textAlign: 'right' }}>Amount</span>
                  </div>
                  {payouts.map((payout) => (
                    <div key={payout.id} style={{ ...styles.ledgerRow, gridTemplateColumns: PAYOUTS_GRID }}>
                      <span style={styles.ledgerCell}>{formatDate(payout.createdAt)}</span>
                      <span style={styles.ledgerCell}>
                        {payout.bankName && payout.bankLast4
                          ? `${payout.bankName} ****${payout.bankLast4}`
                          : 'Bank account'}
                      </span>
                      <span>
                        <span style={{
                          ...styles.statusBadge,
                          ...getPayoutStatusStyle(payout.status),
                        }}>
                          {getStatusLabel(payout.status)}
                        </span>
                        {payout.status === 'FAILED' && payout.failureReason && (
                          <span style={styles.failureReason}>{payout.failureReason}</span>
                        )}
                      </span>
                      <span style={{
                        ...styles.ledgerCell,
                        textAlign: 'right',
                        fontWeight: 600,
                        color: colors.ink.black,
                      }}>
                        {formatCurrency(payout.amount)}
                      </span>
                    </div>
                  ))}
                </div>
                </div>
                <PaginationControls
                  currentPage={payoutsPage}
                  totalPages={payoutsTotalPages}
                  onPageChange={setPayoutsPage}
                />
              </>
            )
          )}
      </div>

      {showPayoutModal && balance && (
        <RequestPayoutModal
          availableBalance={balance.availableBalance}
          bankName={stripeStatus?.bankName ?? null}
          bankLast4={stripeStatus?.bankLast4 ?? null}
          onClose={() => setShowPayoutModal(false)}
          onSuccess={handlePayoutSuccess}
        />
      )}
    </SellerLayout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  topRow: {
    display: 'grid',
    gridTemplateColumns: '3fr 2fr',
    gap: '24px',
  },
  balanceSection: {
    padding: '20px',
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
  },
  sectionTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: 700,
    color: colors.ink.black,
  },
  balanceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  payoutButton: {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  loading: {
    padding: '20px 0',
    color: colors.gray[600],
  },
  error: {
    padding: '12px',
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '8px',
    fontSize: '14px',
  },
  totalBalance: {
    fontSize: '32px',
    fontWeight: 700,
    color: colors.ink.black,
  },
  detailsCard: {
    padding: '16px',
    backgroundColor: colors.gray[100],
    borderRadius: '16px',
    border: `1px solid ${colors.gray[200]}`,
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  detailLine: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
  },
  detailAmount: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  detailLabel: {
    fontSize: '14px',
    color: colors.ink.black,
  },
  detailHint: {
    fontSize: '14px',
    color: colors.gray[600],
  },
  divider: {
    height: '1px',
    backgroundColor: colors.gray[300],
    margin: '12px 0',
  },
  tabRow: {
    display: 'flex',
    gap: '4px',
    marginBottom: '16px',
    padding: '4px',
    backgroundColor: colors.gray[200],
    borderRadius: '10px',
  },
  tabButton: {
    flex: 1,
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.gray[600],
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  tabButtonActive: {
    color: colors.ink.black,
    backgroundColor: colors.white,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
  },
  emptyState: {
    padding: '40px 0',
    textAlign: 'center',
    color: colors.gray[600],
  },
  ledgerTable: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    overflow: 'hidden',
  },
  ledgerHeader: {
    display: 'grid',
    gap: '16px',
    padding: '12px 16px',
    backgroundColor: colors.gray[100],
    borderRadius: '8px 8px 0 0',
    fontWeight: 600,
    fontSize: '12px',
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  ledgerRow: {
    display: 'grid',
    gap: '16px',
    padding: '14px 16px',
    borderBottom: `1px solid ${colors.gray[200]}`,
    alignItems: 'center',
    transition: 'background-color 0.15s',
  },
  ledgerCell: {
    fontSize: '14px',
    color: colors.ink.black,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 600,
  },
  failureReason: {
    display: 'block',
    fontSize: '12px',
    color: '#DC2626',
    marginTop: '4px',
  },
};
